// OpenRouter API client for AI-powered layout optimization

const API_KEY = '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'
const API_URL = 'https://openrouter.ai/api/v1/chat/completions'

// Free models to try in order of preference
const FREE_MODELS = [
  'meta-llama/llama-3.3-70b-instruct:free',
  'mistralai/mistral-small-3.1-24b-instruct:free',
  'google/gemma-3-12b-it:free'
]

/**
 * Fetch optimized layout from OpenRouter AI
 * @param {Object} screenInfo - Screen dimensions and properties
 * @returns {Promise<Object>} - Layout configuration
 */
export async function fetchLayoutFromOpenRouter(screenInfo) {
  // Build the prompt for the AI
  const prompt = buildPrompt(screenInfo)
  
  // Try each model in order until one succeeds
  for (const model of FREE_MODELS) {
    try {
      console.log(`Trying model: ${model}`)
      const response = await fetchWithRetry(model, prompt)
      
      if (response) {
        console.log(`Successfully got layout from ${model}`)
        return response
      }
    } catch (error) {
      console.warn(`Model ${model} failed:`, error.message)
      continue
    }
  }
  
  throw new Error('All AI models failed to respond')
}

/**
 * Build the AI prompt with screen information
 */
function buildPrompt(screenInfo) {
  const { width, height, ratio, dpr } = screenInfo
  
  return `Screen detected: ${width}x${height}px
Aspect ratio: ${ratio.toFixed(2)} (${ratio < 1 ? 'portrait' : ratio > 1.5 ? 'landscape' : 'square'})
Device pixel ratio: ${dpr}

This is a breathing exercise kiosk app with 4 cards:
- CALM DOWN
- RELAX  
- RECHARGE
- BOOST

The app displays these 4 cards in a grid layout. Users tap cards to play breathing exercise videos.

VERTICAL SCREEN VIDEO LINKS (for portrait/aspect ratio < 1):
- CALM DOWN: https://kewaar2026transcoded.media-storage.eu-central.qencode.com/CALM%20DOWN%20VERTICAL/playlist.m3u8
- RELAX: https://kewaar2026transcoded.media-storage.eu-central.qencode.com/PANIC%20ATTACK%20VERTICAL/playlist.m3u8 (Panic Attack is the RELAX course)
- RECHARGE: https://kewaar2026transcoded.media-storage.eu-central.qencode.com/RECHARGE%20VERTICAL/playlist.m3u8
- BOOST: https://kewaar2026transcoded.media-storage.eu-central.qencode.com/BOOST%20VERTICAL/playlist.m3u8

OPTIMIZE THE LAYOUT for this specific screen size:
- Maximize card size for easy touch interaction
- Ensure text is readable from a distance
- Use appropriate spacing
- Consider the aspect ratio
- For vertical/portrait screens (ratio < 1), use '1x4' gridTemplate to stack cards vertically
- For landscape screens (ratio > 1.5), use '4x1' or '2x2' gridTemplate

Return ONLY a JSON object with these exact properties:
{
  "gridHeight": "CSS vh value (e.g., '75vh', '80vh')",
  "gridWidth": "CSS width (e.g., '95%', '1000px', 'min(90%, 1200px)')",
  "cardGap": "CSS gap value (e.g., '20px', '24px', 'clamp(16px, 2vw, 32px)')",
  "titleSize": "CSS font-size (e.g., '2.5rem', 'clamp(2rem, 5vw, 4rem)')",
  "padding": "CSS padding (e.g., '40px 30px', '60px 40px')",
  "gridTemplate": "Layout type: '2x2' (4 cards in 2x2 grid), '1x4' (4 cards stacked vertically), or '4x1' (4 cards in horizontal row)",
  "centered": "boolean (true/false) - whether to center the grid on screen. Default: true",
  "reasoning": "Brief explanation of your choices"
}

IMPORTANT: Return ONLY the JSON object, no markdown, no explanations outside the JSON.`
}

/**
 * Fetch from OpenRouter API with retry logic
 */
async function fetchWithRetry(model, prompt, maxRetries = 3) {
  let lastError
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Kewaar Breathing App'
        },
        body: JSON.stringify({
          model: model,
          messages: [
            {
              role: 'system',
              content: 'You are a CSS layout expert specializing in responsive design for kiosks and touch screens. Always return valid JSON only.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 500
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error?.message || `HTTP ${response.status}`)
      }
      
      const data = await response.json()
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid response structure from API')
      }
      
      const content = data.choices[0].message.content
      
      // Parse the JSON response
      const layout = parseAIResponse(content)
      
      if (layout) {
        return layout
      }
      
      throw new Error('Failed to parse AI response')
      
    } catch (error) {
      lastError = error
      console.warn(`Attempt ${attempt} failed:`, error.message)
      
      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000
        console.log(`Retrying in ${delay}ms...`)
        await sleep(delay)
      }
    }
  }
  
  throw lastError
}

/**
 * Parse AI response and extract JSON
 */
function parseAIResponse(content) {
  try {
    // Try to find JSON in the response (in case there's extra text)
    const jsonMatch = content.match(/\{[\s\S]*\}/)
    
    if (!jsonMatch) {
      console.error('No JSON found in response:', content)
      return null
    }
    
    const jsonStr = jsonMatch[0]
    const parsed = JSON.parse(jsonStr)
    
    // Validate required fields
    const required = ['gridHeight', 'gridWidth', 'cardGap', 'titleSize', 'padding', 'gridTemplate']
    const missing = required.filter(field => !parsed[field])
    
    if (missing.length > 0) {
      console.error('Missing required fields:', missing)
      return null
    }
    
    // Validate gridTemplate value
    const validTemplates = ['2x2', '1x4', '4x1']
    if (!validTemplates.includes(parsed.gridTemplate)) {
      console.warn('Invalid gridTemplate, defaulting to 2x2')
      parsed.gridTemplate = '2x2'
    }
    
    return parsed
    
  } catch (error) {
    console.error('Error parsing AI response:', error)
    console.error('Raw content:', content)
    return null
  }
}

/**
 * Sleep utility for delays
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}